import React, { useState, useRef, useEffect } from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import {
	CaretDownIcon,
	ArrowRightIcon,
	HamburgerMenuIcon,
} from '@radix-ui/react-icons';
import { Link } from 'gatsby';
import { Logo } from '../../atom/logo/Logo';
import { useNavigation } from '../../../hooks/useNavigation';
import {
	GatsbyImage,
	IGatsbyImageData,
	StaticImage,
} from 'gatsby-plugin-image';
import { useContacts } from '../../../hooks/useContacts';
import { PhoneIcon } from '../../atom/icons/PhoneIcon';
import { EmailIcon } from '../../atom/icons/EmailIcon';
import { useGlobal } from '../../../hooks/useGlobal';
import { Button } from '../../atom/button/Button';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import clsx from 'clsx';
import './style.css';
import { pushDataLayer } from '../../../utils/tracking';
import { EarlypayLogo } from '../../atom/logo/EarlypayLogo';
import { ProductIcon } from '../../molecules/productTile/ProductTile';

const isBrowser = typeof window !== undefined;
type NavigationProps = {
	theme: 'dark' | 'light';
	brand?: 'default' | 'xero' | 'myob' | 'vow';
};

export const Navigation = ({ theme = 'dark', brand }: NavigationProps) => {
	const [viewportDirection, setViewportDirection] = useState<
		'justify-start' | 'justify-end'
	>('justify-start');

	const [viewportWidth, setViewportWidth] = useState(0);
	const { products } = useNavigation();

	const [showNav, setShowNav] = useState<boolean>(false);
	const [forceLightTheme, setForceLightTheme] = useState<boolean>(false);
	const [viewportMargin, setViewportMargin] = useState<number>(0);
	const mode =
		forceLightTheme || (showNav && viewportWidth < 900) ? 'light' : theme;

	useEffect(() => {
		if (!isBrowser) {
			return;
		}

		function handleResize() {
			setViewportWidth(window.innerWidth);
		}

		function handleScroll() {
			setForceLightTheme(window.pageYOffset >= 80);
		}

		// Initialize
		handleResize();

		window.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('resize', handleResize, { passive: true });
		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const getToKnowUsLinks = [
		{ title: 'About', path: '/about' },
		{ title: 'Investors', path: '/investors' },
	];
	return (
		<NavigationMenu.Root
			className={'navigation'}
			data-theme={mode}
			data-brand={brand}
		>
			<div className={clsx('navigation__inner', { '--active': showNav })}>
				<div
					className={clsx(
						'navigation__logo-menu',
						`--${mode}`,
						{ 'navigation__logo-menu__bg-light': forceLightTheme },
						{ '--active': showNav }
					)}
				>
					<Link to="/" className={'shrink-0 flex items-center mr-10'}>
						<VisuallyHidden>Earlypay</VisuallyHidden>
						<EarlypayLogo color={mode === 'dark' ? 'white' : 'blue'} />
					</Link>

					<button
						className={clsx('navigation__menu-btn', `--${mode}`)}
						onClick={() => setShowNav(!showNav)}
					>
						<VisuallyHidden>Menu</VisuallyHidden>
						<HamburgerMenuIcon />
					</button>
				</div>
				<div className={'navigation__menu__outer-wrapper'}>
					<NavigationMenu.List className={'navigation__menu__wrapper'}>
						<li
							className={'navigation__menu__list'}
							onMouseOver={() => setViewportDirection('justify-start')}
						>
							<ul className={'navigation__menu__list'}>
								<NavigationMenu.Item className={'navigation__menu__list-item'}>
									<NavMenuTrigger
										title={'What We Do'}
										onMouseOver={setViewportMargin}
									/>
									<NavigationMenu.Content
										className={'navigation__menu__content'}
									>
										<ul className={'menu__list'} data-content="what-we-do">
											<div
												className={'menu__content__heading'}
												style={{ gridColumn: 'span 2' }}
											>
												Our Products
											</div>
											{products.map(
												(product: {
													title: string;
													tagline: string;
													image: { gatsbyImageData: IGatsbyImageData };
													link: string;
													icon: string;
												}) => (
													<ProductListItem
														title={product.title}
														tagline={product.tagline}
														image={product.image}
														link={`/what-we-do${product.link}`}
														icon={product.iconName}
														key={product.link}
													/>
												)
											)}
										</ul>
									</NavigationMenu.Content>
								</NavigationMenu.Item>

								<NavigationMenu.Item className={'navigation__menu__list-item'}>
									<NavMenuTrigger
										title={'Company'}
										onMouseOver={setViewportMargin}
									/>
									<NavigationMenu.Content
										className={'navigation__menu__content'}
									>
										<div
											className={
												'py-6 px-0 flex flex-col desktop-nav:pt-4 desktop-nav:px-5 desktop-nav:pb-5 desktop-nav:w-[200px]'
											}
										>
											<ul>
												<p className={'menu__content__heading mb-4'}>
													Get to know us
												</p>
												{getToKnowUsLinks.map(
													(item: { title: string; path: string }) => (
														<SubmenuLink {...item} key={item.path} />
													)
												)}
											</ul>
										</div>
									</NavigationMenu.Content>
								</NavigationMenu.Item>

								<NavigationMenu.Item className={'navigation__menu__list-item'}>
									<NavMenuTrigger
										title={'Resources'}
										onMouseOver={setViewportMargin}
									/>
									<NavigationMenu.Content
										className={'navigation__menu__content'}
									>
										<div
											className={
												'py-6 px-0 flex flex-col desktop-nav:pt-4 desktop-nav:px-5 desktop-nav:pb-5 desktop-nav:w-[200px]'
											}
										>
											<ul>
												<SubmenuLink title="Blog" path="/blog" />
												<SubmenuLink
													title="Case Studies"
													path="/case-studies"
												/>
												<Link
													to="/resources"
													className="mt-4 flex items-center text-fineprint font-medium hover:text-ep-blue group"
												>
													<span>See overview</span>
													<ArrowRightIcon
														className="ml-1 text-neutral-400 group-hover:text-ep-blue transition-transform group-hover:translate-x-1"
														width={12}
													/>
												</Link>
											</ul>
										</div>
									</NavigationMenu.Content>
								</NavigationMenu.Item>

								<NavigationMenu.Item className={'navigation__menu__list-item'}>
									<Link to="/partners" className="navigation__menu__trigger">
										Partners
									</Link>
									{/* <NavMenuTrigger
										title={'Partners'}
										onMouseOver={setViewportMargin}
									/>
									<NavigationMenu.Content
										className={'navigation__menu__content'}
									>
										<div
											className={
												'py-6 px-0 flex flex-col desktop-nav:p-1 desktop-nav:w-[280px]'
											}
										>
											<PartnerCard />
										</div>
									</NavigationMenu.Content> */}
								</NavigationMenu.Item>

								<NavigationMenu.Item className={'navigation__menu__list-item'}>
									<NavMenuTrigger
										title={'Integrations'}
										onMouseOver={setViewportMargin}
									/>
									<NavigationMenu.Content
										className={'navigation__menu__content'}
									>
										<div
											className={
												'py-6 px-0 flex flex-col desktop-nav:pt-4 desktop-nav:px-5 desktop-nav:pb-5 desktop-nav:w-[160px]'
											}
										>
											<ul>
												<SubmenuLink title="MYOB" path="/myob" />
												<SubmenuLink title="Xemplo" path="/xemplo" />
												<SubmenuLink title="Xero" path="/xero" />
												<Link
													to="/integrations"
													className="mt-4 flex items-center text-fineprint font-medium hover:text-ep-blue group"
												>
													<span>See all</span>
													<ArrowRightIcon
														className="ml-1 text-neutral-400 group-hover:text-ep-blue transition-transform group-hover:translate-x-1"
														width={12}
													/>
												</Link>
											</ul>
										</div>
									</NavigationMenu.Content>
								</NavigationMenu.Item>
							</ul>
						</li>

						<li
							className={'navigation__menu__list'}
							onMouseOver={() => setViewportDirection('justify-end')}
						>
							<ul className={'navigation__menu__list'}>
								<NavigationMenu.Item className={'navigation__menu__list-item'}>
									<NavigationMenu.Trigger
										className={'navigation__menu__trigger'}
									>
										Contact
										<CaretDownIcon
											aria-hidden
											className={'navigation__caret'}
										/>
									</NavigationMenu.Trigger>
									<NavigationMenu.Content
										className={'navigation__menu__content'}
									>
										<ContactsMenu />
									</NavigationMenu.Content>
								</NavigationMenu.Item>
								<NavigationMenu.Item className={'navigation__menu__list-item'}>
									<NavigationMenu.Trigger
										className={'navigation__menu__trigger'}
									>
										Login
										<CaretDownIcon
											aria-hidden
											className={'navigation__caret'}
										/>
									</NavigationMenu.Trigger>
									<NavigationMenu.Content
										className={'navigation__menu__content'}
									>
										<LoginsMenu />
									</NavigationMenu.Content>
								</NavigationMenu.Item>
								<NavigationMenu.Item className={'navigation__menu__list-item'}>
									<Link
										to={'/sign-up'}
										className={
											'navigation__menu__trigger navigation__menu__cta'
										}
									>
										Sign Up
									</Link>
								</NavigationMenu.Item>
							</ul>
						</li>
					</NavigationMenu.List>
				</div>
				<NavigationMenu.Indicator className={'navigation__indicator'}>
					<div className={'navigation__indicator__icon'} />
				</NavigationMenu.Indicator>
				{viewportWidth >= 900 ? (
					<div
						className={clsx('navigation__viewport-position', viewportDirection)}
					>
						<NavigationMenu.Viewport
							className={'navigation__menu__viewport'}
							style={{ marginLeft: viewportMargin + 123 }} // 123 is the estimated logo width
						/>
					</div>
				) : null}
			</div>
		</NavigationMenu.Root>
	);
};

const NavMenuTrigger = ({
	title,
	onMouseOver,
}: {
	title: string;
	onMouseOver?: (state: number) => void;
}) => {
	const ref = useRef(null);
	const [offsetLeft, setOffsetLeft] = useState(0);

	useEffect(() => {
		if (ref.current) {
			setOffsetLeft(ref.current.offsetLeft);
		}
	}, []);
	return (
		<NavigationMenu.Trigger
			className={'navigation__menu__trigger'}
			ref={ref}
			onMouseOver={() => (onMouseOver ? onMouseOver(offsetLeft) : null)}
		>
			{title}
			<CaretDownIcon aria-hidden className={'navigation__caret'} />
		</NavigationMenu.Trigger>
	);
};

const ProductListItem = ({ title, tagline, image, link, icon }) => (
	<li>
		<Link to={link} className={'menu__product-list-item'}>
			<figure className="w-12 h-12 overflow-hidden shrink-0 mr-4">
				<ProductIcon icon={icon} size={48} />
				{icon}
			</figure>
			<div className={'flex flex-col'}>
				<p className={'menu__product-list-item__title'}>{title}</p>
				<p className={'text-fineprint text-ep-navy leading-snug font-light'}>
					{tagline}
				</p>
			</div>
		</Link>
	</li>
);

type SubmenuLinkProps = {
	title: string;
	path: string;
};

const SubmenuLink = ({ title, path }: SubmenuLinkProps) => {
	return (
		<li className={'submenu__list-item'}>
			<Link to={path} className={'submenu__link'}>
				<ArrowRightIcon className={'submenu__link__icon'} />
				<span>{title}</span>
			</Link>
		</li>
	);
};

const PartnerCard = () => (
	<Link to="/partners" className={'menu__partner-card'}>
		<div className={'menu__partner-card__text'}>
			Partner with Earlypay{' '}
			<ArrowRightIcon strokeWidth={2} width={10} className={'ml-1'} />
		</div>
		<StaticImage
			src="../../../images/partner-card.jpg"
			alt=""
			objectFit="cover"
			className={'menu__partner-card__img'}
			style={{ position: 'absolute' }}
		/>
	</Link>
);

const ContactsMenu = () => {
	const { contacts } = useContacts();

	return (
		<ul className={'menu__contacts'}>
			{contacts.map(
				(contact: {
					title: string;
					contactNumber: string;
					email: string;
					event: 'string';
					eventTrigger: 'phone' | 'email';
				}) => (
					<ContactCard
						title={contact.title}
						telNo={contact.contactNumber}
						email={contact.email}
						event={contact.event}
						eventTrigger={contact.eventTrigger}
						key={contact.email + '-' + contact.title}
					/>
				)
			)}
		</ul>
	);
};

const ContactCard = ({
	title,
	telNo,
	email,
	event,
	eventTrigger,
}: {
	title: string;
	telNo: string;
	email: string;
	event: string;
	eventTrigger: 'phone' | 'email';
}) => (
	<li className={'flex flex-col mb-8 last:mb-0'}>
		<div className={'text-fineprint font-normal text-ep-navy mb-2'}>
			{title}
		</div>
		<a
			href={`tel:${telNo.replace(/\s/g, '')}`}
			className={'navigation__contact-card__link'}
			onClick={() => (eventTrigger === 'phone' ? pushDataLayer(event) : null)}
		>
			<PhoneIcon
				width={16}
				height={16}
				className={'navigation__contact-card__icon'}
			/>
			{telNo}
		</a>
		<a
			href={`mailto:${email}`}
			className={'navigation__contact-card__link'}
			onClick={() => (eventTrigger === 'email' ? pushDataLayer(event) : null)}
		>
			<EmailIcon
				width={16}
				height={16}
				className={'navigation__contact-card__icon'}
			/>
			<div className="text-ellipsis overflow-hidden">{email}</div>
		</a>
	</li>
);

const LoginsMenu = () => {
	const { global } = useGlobal();

	const links = [
		{
			title: 'Earlypay',
			url: global.earlypayLoginLink,
		},
		{
			title: 'Cashflow Finance',
			url: global.cashflowFinanceLoginLink,
		},
		{
			title: 'Timelio',
			url: global.timelioLink,
		},
	];

	return (
		<ul className={'menu__login'}>
			{links.map((link) => (
				<LoginCard {...link} key={link.url} />
			))}
		</ul>
	);
};

const LoginCard = ({ title, url }: { title: string; url: string }) => {
	const getHost = (url: string) => {
		const newUrl = new URL(url);

		return newUrl.host;
	};
	return (
		<li className={'last:mb-0'}>
			<a href={url} target="_blank" className={'menu__login-card'}>
				<div className={'menu__login-card__title'}>{title}</div>
				<div className={'menu__login-card__link'}>{getHost(url)}</div>
			</a>
		</li>
	);
};
